import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import type { ProjectModel } from '@xver/apis/v1/projects/model';
import { Button } from '@xver/components/Atoms';
import CreateProjectModal from '@xver/pages/private/account/ProjectsPage/components/CreateProjectModal';
import ProjectsTable from '@xver/pages/private/account/ProjectsPage/components/ProjectsTable';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const PREVIEW_COUNT = 5;

interface ProjectsOverviewProps {
    projects: ProjectModel[] | undefined;
    isLoading: boolean;
    /** Also true while a retry after a failure is in flight, so the alert and its Retry stay. */
    isError: boolean;
    onRetry: () => void;
    isRetrying: boolean;
}

export default function ProjectsOverview({
    projects,
    isLoading,
    isError,
    onRetry,
    isRetrying,
}: ProjectsOverviewProps) {
    const { t } = useTranslation(['projects', 'dashboard']);
    const [createOpen, setCreateOpen] = useState(false);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const retried = useRef(false);
    const total = projects?.length ?? 0;
    const failed = isError && !projects;

    useEffect(() => {
        if (failed || !retried.current) return;
        retried.current = false;
        headingRef.current?.focus();
    }, [failed]);

    return (
        <div className="w-full flex flex-col justify-start items-start gap-1">
            <div className="w-full flex flex-col sm:flex-row justify-between sm:items-end gap-3">
                <div className="flex flex-col gap-1">
                    <h2 ref={headingRef} tabIndex={-1} className="text-xl">
                        {t('list.tableTitle')}
                    </h2>
                    <p className="text-sm text-fg/68">{t('list.tableDescription')}</p>
                </div>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<IconPlus size={16} aria-hidden="true" />}
                    onClick={() => setCreateOpen(true)}
                >
                    {t('create.open')}
                </Button>
            </div>
            <div className="mt-3.5 w-full">
                {failed ? (
                    <div role="alert" className="w-full py-4 flex flex-col items-start gap-3">
                        <p className="flex items-center gap-2 text-sm text-fg">
                            <IconAlertCircle size={18} aria-hidden="true" />
                            {t('list.loadFailed')}
                        </p>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                retried.current = true;
                                onRetry();
                            }}
                            isLoading={isRetrying}
                        >
                            {t('common.retry')}
                        </Button>
                    </div>
                ) : (
                    <ProjectsTable
                        projects={projects?.slice(0, PREVIEW_COUNT)}
                        isLoading={isLoading}
                    />
                )}
            </div>
            <div className="mt-2 w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                {total > PREVIEW_COUNT && (
                    <p className="text-fg/68">
                        {t('dashboard:projects.showing', { shown: PREVIEW_COUNT, total })}
                    </p>
                )}
                <Link
                    to="/projects"
                    className="min-h-6 inline-flex items-center text-fg/78 underline hover:text-fg"
                >
                    {t('switcher.manage')}
                </Link>
            </div>
            <CreateProjectModal opened={createOpen} onClose={() => setCreateOpen(false)} />
        </div>
    );
}
